import { Globe, Lock } from 'lucide-react';
import { useWorkArea } from '@/contexts/WorkAreaContext';
import { cn } from '@/lib/utils';
import type { WorkArea } from '@/lib/api';

function formatDate(value: string): string {
  return value.slice(0, 19).replace('T', ' ');
}

function ownerLabel(workArea: WorkArea): string {
  return workArea.ownerLogin ?? workArea.ownerId ?? 'Unassigned';
}

export function WorkAreaList(): React.ReactElement {
  const {
    selectedWorkAreaId,
    setSelectedWorkAreaId,
    workAreas,
    isLoadingWorkAreas,
    isErrorWorkAreas,
    workAreasError,
  } = useWorkArea();

  if (isLoadingWorkAreas) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-text-secondary">
        Loading work areas…
      </div>
    );
  }

  if (isErrorWorkAreas) {
    return (
      <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
        Failed to load work areas
        {workAreasError ? `: ${workAreasError.message}` : '.'}
      </div>
    );
  }

  if (!workAreas || workAreas.length === 0) {
    return (
      <div className="rounded-md border border-border bg-surface-elevated px-3 py-8 text-center">
        <p className="text-sm font-medium text-text-primary">No work areas yet</p>
        <p className="mt-1 text-sm text-text-secondary">
          Create a work area to group codebases and artifacts.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-inset text-left">
            {['Name', 'Visibility', 'Owner', 'Created'].map(h => (
              <th
                key={h}
                className="px-3.5 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workAreas.map(workArea => {
            const selected = workArea.id === selectedWorkAreaId;
            const isTeam = workArea.visibility === 'team';
            return (
              <tr
                key={workArea.id}
                role="button"
                tabIndex={0}
                aria-current={selected ? 'true' : undefined}
                aria-label={`Select work area ${workArea.name}`}
                className={cn(
                  'cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-surface-elevated focus:bg-surface-elevated focus:outline-none focus:ring-1 focus:ring-accent-bright focus:ring-inset',
                  selected &&
                    'border-l-2 border-l-accent-bright bg-[oklch(0.72_0.14_225/0.08)] hover:bg-[oklch(0.72_0.14_225/0.12)]'
                )}
                onClick={(): void => {
                  setSelectedWorkAreaId(workArea.id);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                    event.preventDefault();
                    setSelectedWorkAreaId(workArea.id);
                  }
                }}
              >
                <td className="px-3.5 py-3">
                  <div
                    className="truncate text-sm font-semibold text-text-primary"
                    title={workArea.name}
                  >
                    {workArea.name}
                  </div>
                  {workArea.description && (
                    <div
                      className="mt-0.5 truncate text-[11px] text-text-tertiary"
                      title={workArea.description}
                    >
                      {workArea.description}
                    </div>
                  )}
                </td>
                <td className="px-3.5 py-3">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10.5px]',
                      isTeam
                        ? 'border-[oklch(0.72_0.14_225/0.25)] bg-[oklch(0.72_0.14_225/0.12)] text-accent-bright'
                        : 'border-border bg-surface-elevated text-text-secondary'
                    )}
                  >
                    {isTeam ? <Globe className="size-2.5" /> : <Lock className="size-2.5" />}
                    {workArea.visibility}
                  </span>
                </td>
                <td
                  className="truncate px-3.5 py-3 font-mono text-[11.5px] text-text-secondary"
                  title={ownerLabel(workArea)}
                >
                  {ownerLabel(workArea)}
                </td>
                <td className="truncate px-3.5 py-3 font-mono text-[11.5px] text-text-tertiary">
                  {formatDate(workArea.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
